<section id="{{ renderMode }}">
  <header [formGroup]="outputFilenameFormGroup">
    @if (renderMode === 'web-app') {
      <h1 mat-dialog-title class="has-icon">
        <mat-icon fontSet="material-icons-outlined">widgets</mat-icon>
        AwesomeDoc created
      </h1>
    }
    @if (renderMode === 'web-app' && this.outputFilenameFormGroup) {
      <inline-editor-lib
        formControlName="outputFilename"
        [allowEdit]="true"
        [showEditIcon]="outputFilenameFormGroup.get('outputFilename')?.value"
        editIconTooltip="Edit output filename"
        [errorMessage]="outputFilenameErrorMessage"
        [viewOpacity]="0.8"
      ></inline-editor-lib>
    }
    @if (actingAs) {
      <span
        class="acting-as"
        matTooltip="To change the user, close this dialog and click the person icon in the main header. The 'Create AwesomeDoc' button will generate a fresh document."
        matTooltipPosition="below"
        [matTooltipShowDelay]="500"
      >
        <mat-icon fontIcon="person" class="acting-as-icon"></mat-icon>
        Acting as {{ actingAs.firstName }} {{ actingAs.lastName }}
      </span>
    }
    <mat-icon
      fontIcon="close"
      class="close-dialog"
      (click)="onCancelClick()"
      [matTooltip]="'Cancel creating ' + data.templateWorkflowConfig.name + ' AwesomeDoc'"
      matTooltipShowDelay="500"
      tabindex="0"
      (keydown.enter)="onCancelClick()"
    ></mat-icon>
  </header>
  <div mat-dialog-content>
    <h2 class="heading-underline" [ngClass]="renderMode === 'chrome-extension' ? 'chrome-ext-center' : ''">
      <span>What would you like to do with the file?</span>
      @if (showAiContentNotice && renderMode !== 'chrome-extension') {
        <span class="ai-content-notice" role="note">
          <mat-icon fontIcon="auto_awesome"></mat-icon>
          This AwesomeDoc may contain AI generated content
        </span>
      }
    </h2>
    <main>
      <section id="basic-actions" class="buttons">
        <button id="preview-btn" theme="landing-page-large"
          [class.full-width]="!showDocumentOptions()"
          (click)="onEditClick()" [disabled]="actionInProgress"
          [matTooltip]="'Open the document in Word to preview and edit'" matTooltipShowDelay="500" matTooltipPosition="below">
          <mat-icon fontSet="material-icons-outlined">description</mat-icon>
          <mat-icon fontSet="material-icons-outlined" class="background-icon">description</mat-icon>
          <div class="app-desc">
            <h2 class="app-title red">Preview and edit</h2>
            <p>Preview and edit the document in Word before proceeding</p>
          </div>
        </button>
        @if (showDocumentOptions()) {
          <aside id="save-send-to-entity">
          <div class="app-desc">
            <div class="toggle-title">
              <h2 class="app-title with-toggle">
                Select format
                <mat-button-toggle-group
                  id="word-pdf-both"
                  class="small-icons-only"
                  [value]="documentType"
                  (change)="onOutputFileTypeToggleChange($event)"
                  [hideSingleSelectionIndicator]="true"
                >
                  <mat-button-toggle
                    class="word-toggle"
                    [value]="'docx'"
                    [matTooltip]="'Save/Email as a Word Document (.docx)'"
                    matTooltipShowDelay="500"
                  >
                    <app-filetype-icon [value]="'docx'"></app-filetype-icon>
                    Word
                  </mat-button-toggle>
                  <mat-button-toggle class="pdf-toggle" [value]="'pdf'" [matTooltip]="'Save/Email as a PDF'" matTooltipShowDelay="500">
                    <app-filetype-icon [value]="'pdf'"></app-filetype-icon>
                    PDF
                  </mat-button-toggle>
                  <mat-button-toggle
                    class="both-toggle"
                    [value]="'docx,pdf'"
                    [matTooltip]="'Save/Email as both a Word Document (.docx) and PDF'"
                    matTooltipShowDelay="500"
                  >
                    <app-filetype-icon [value]="'docx'"></app-filetype-icon>
                    <app-filetype-icon [value]="'pdf'"></app-filetype-icon>
                    Both
                  </mat-button-toggle>
                </mat-button-toggle-group>
              </h2>
            </div>
            <section id="save-send-buttons">
              @for (action of actions; track action) {
                @if (action.id === 'save-to-entity-btn' || (showEmailOption && action.id === 'email-to-btn')) {
                  <button
                    [id]="action.id"
                    theme="landing-page-medium"
                    [matTooltip]="outputFilenameErrorTooltipMessage ? outputFilenameErrorTooltipMessage : action.tooltip"
                    matTooltipPosition="below"
                    matTooltipShowDelay="500"
                    (click)="onDocumentActionClick(action.action)"
                    [disabled]="actionInProgress || !data.entitySelection.destinations.length || outputFilenameHasError"
                  >
                    <mat-icon fontSet="material-icons-outlined" class="background-icon">{{ action.icon }}</mat-icon>
                    <div class="app-desc">
                      <h2 class="app-title red">
                        <mat-icon fontSet="material-icons-outlined">{{ action.icon }}</mat-icon>
                        {{ action.title }}
                      </h2>
                      @if (action.id !== 'email-to-btn') {
                        <ul class="save-to-list">
                          @for (dst of data.entitySelection.destinations; track dst) {
                            <li>
                              @if (findEntityMeta(dst['type'])?.iconUrl; as iconUrl) {
                                <app-salesforce-entity-icon
                                  [iconUrl]="iconUrl"
                                  [iconColor]="findEntityMeta(dst['type'])?.iconColor"
                                  [iconClass]="getIcon(dst['type'])"
                                  imgClass="entity-icon-img"
                                  [tooltip]="getEntityLabel(dst['type']) | titlecase"
                                >
                                </app-salesforce-entity-icon>
                              } @else {
                                <i
                                  class="{{ getIcon(dst['type']) }} {{ dst['type'] }} entity-icon"
                                  [ngClass]="renderMode === 'chrome-extension' ? 'white' : ''"
                                  [matTooltip]="getEntityLabel(dst['type']) | titlecase"
                                  matTooltipShowDelay="500"
                                ></i>
                              }
                              {{ dst['name'] }}
                            </li>
                          }
                        </ul>
                      }
                    </div>
                  </button>
                }
              }
            </section>
          </div>
        </aside>
        }
      </section>
      @if (!!signatureConfig) {
        <section class="docusign-container">
          <h2 [ngClass]="renderMode === 'chrome-extension' ? 'section-heading' : ''">
            <mat-icon fontIcon="draw"></mat-icon><span id="signature-service-name">Docusign</span>
            <!-- CHROME EXTENSION -->
            @if (renderMode === 'chrome-extension') {
              <span
                class="sub-info popup-link"
                [ngClass]="openDialog ? 'popup-open' : ''"
                (click)="openDialog = !openDialog"
                tabindex="0"
                (keydown.enter)="openDialog = !openDialog"
                (keydown.space)="openDialog = !openDialog"
                [matTooltip]="'Edit recipients and email before sending'"
                matTooltipShowDelay="500"
              >
                <span>Edit Config </span><mat-icon fontIcon="settings"></mat-icon>
              </span>
            }
          </h2> <!-- disable this -->
          @if (openDialog) {
            <app-popup-dialog
              [formGroup]="formGroup"
              [recipientInfoService]="recipientInfoService"
              [namespace]="data.templateWorkflowConfig.clientNamespace"
              [emailBody]="signatureConfig.emailBody"
              [emailSubject]="signatureConfig.emailSubject"
              [recipients]="signatureConfig.recipients"
              [provider]="signatureConfig.provider"
              [usingDocusignTemplate]="usingDocusignTemplate"
              (openDialog)="openDialog = $event"
            >
            </app-popup-dialog>
          }
          <!-- WEB APP -->
          <section>
            <!-- TODO: Instead of passing the namespace - grab from the local storage? -->
            @if (renderMode === 'web-app') {
              <app-edit-config
                [eFormGroup]="formGroup"
                [recipientInfoService]="recipientInfoService"
                [namespace]="data.templateWorkflowConfig.clientNamespace"
                [emailBody]="signatureConfig.emailBody"
                [emailSubject]="signatureConfig.emailSubject"
                [recipients]="signatureConfig.recipients"
                [provider]="signatureConfig.provider"
                [usingDocusignTemplate]="usingDocusignTemplate"
              >
              </app-edit-config>
            }
          </section>
          @if (showOverlay) {
            <div class="overlay"></div>
          }
          <!-- COMMON -->
          <section id="e-signature-actions" class="buttons" [ngClass]="{ 'full-width-send': !showEnvelopeButtonWrapper }">
            <button
              (click)="onSendEnvelopeClick(false)"
              id="send-envelope-btn"
              theme="landing-page-large"
              [matTooltip]="outputFilenameErrorTooltipMessage ? outputFilenameErrorTooltipMessage : 'Send AwesomeDoc for Docusign'"
              matTooltipShowDelay="500"
              [disabled]="actionInProgress || !validConfig || outputFilenameHasError"
            >
              <mat-icon fontSet="material-icons-outlined" [ngClass]="{ invalid: !validConfig }">mail</mat-icon>
              <mat-icon fontSet="material-icons-outlined" class="background-icon">mail</mat-icon>
              @if (!validConfig) {
                <mat-icon
                  [matTooltip]="
                    outputFilenameErrorTooltipMessage
                      ? outputFilenameErrorTooltipMessage
                      : 'Please make sure the following fields are valid in Edit Docusign Config: ' + invalidRecipientDetails
                  "
                  matTooltipShowDelay="500"
                  class="warning-icon"
                  [fontIcon]="renderMode === 'web-app' ? 'error_outline' : 'error'"
                  ></mat-icon
                  >
                }
                <div class="app-desc">
                  <h2 class="app-title red">Send envelope with Docusign</h2>
                  <p>Send AwesomeDoc for Docusign</p>
                </div>
              </button>
              @if (showEnvelopeButtonWrapper) {
                <div class="button-wrapper" [ngClass]="{ 'two-buttons': envelopeUrl, 'stacked-buttons': renderMode === 'chrome-extension'}">
                  <app-envelope-button
                    class="edit-button"
                    id="edit-envelope-btn"
                    [buttonClass]="'edit-button'"
                    [title]="'Edit envelope in Docusign'"
                    [description]="'Edit envelope prior to sending'"
                    [tooltip]="outputFilenameErrorTooltipMessage ? outputFilenameErrorTooltipMessage : 'Edit envelope with latest changes in Docusign, prior to sending'"
                    [invalidTooltip]="'Please make sure the following fields are valid in Edit Docusign Config: ' + invalidRecipientDetails"
                    [disabled]="actionInProgress || !validConfig || outputFilenameHasError"
                    [validConfig]="validConfig"
                    [renderMode]="renderMode"
                    [twoButtons]=!!envelopeUrl
                    (clicked)="onSendEnvelopeClick(true)"
                  ></app-envelope-button>
                  @if (envelopeUrl) {
                    <app-envelope-button
                      class="view-button"
                      id="preview-envelope-btn"
                      [buttonClass]="'view-button'"
                      [title]="'View previous draft in Docusign'"
                      [description]="'Created at ' + envelopeCreatedTime"
                      [tooltip]="'View previous draft in Docusign, prior to sending'"
                      [invalidTooltip]="'Please make sure the following fields are valid in Edit Docusign Config: ' + invalidRecipientDetails"
                      [disabled]="actionInProgress || !validConfig"
                      [validConfig]="validConfig"
                      [renderMode]="renderMode"
                      [twoButtons]=!!envelopeUrl
                      (clicked)="onPreviewEnvelopeDraft(envelopeUrl)"
                    ></app-envelope-button>
                  }
                </div>
              }
            </section>
          </section>
        }
      </main>
    </div>
    <section mat-dialog-actions class="footer-btns one-button">
      <button mat-button (click)="onCancelClick()" theme-style="nav" class="danger-btn-hover"
        [matTooltip]="'Cancel creating ' + data.templateWorkflowConfig.name + 'AwesomeDoc'"
        matTooltipPosition="below" matTooltipShowDelay="500" appRipple>
        CLOSE
      </button> <!-- chanfge to close after an action has happened -->
    </section>
  </section>
